import {
    ArrowTopRightOnSquareIcon,
    EnvelopeIcon,
    PaperAirplaneIcon,
    TrashIcon,
} from "@heroicons/react/24/outline";
import {
    CalendarDate,
    CustomListItemId,
    CustomListKey,
    HomeCareLeadId,
    LayoutSectionParentKey,
    PhoneNumberE164,
    QuestionSource,
    UserId,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { InputFormBlock } from "components/app/input-form-block";
import { NgByline } from "components/byline/ng-byline";
import { Activity } from "components/entity-activity/entity-activity";
import { AntiFeature, Feature } from "components/feature";
import { FilesControl } from "components/files";
import { HomeCareLeadContacts } from "components/home-care-leads/home-care-lead-contacts";
import { HomeCareLeadIntakeTab } from "components/home-care-leads/home-care-lead-intake-tab";
import { HomeCareLeadReferralSource } from "components/home-care-leads/home-care-lead-referral-source";
import { EntityPageHeader } from "components/layout";
import { LayoutFormSection } from "components/layout/layout-form-section";
import { EntityConversationFeed } from "components/messages/entity-conversation-feed";
import { MessageCompose } from "components/messages/message-compose";
import { MessagesTabHeader } from "components/messages/messages-tab-header";
import { Notes } from "components/notes";
import { ActionBarComponent } from "components/shared/action-bar-component";
import { CustomListSelect } from "components/shared/custom-list-select";
import { DetailPageLayout } from "components/shared/detail-page-layout";
import { Col, NgGrid, Row } from "components/shared/grid";
import { PhoneInputE164 } from "components/shared/phone-input-e164";
import { Subnav } from "components/shared/subnav";
import { SubnavItem } from "components/shared/subnav/subnav-types";
import { SelectableUser, UserSelect } from "components/shared/user-select";
import { EntityTasks, TaskBadge } from "components/tasks/child";
import {
    ActionsMenu,
    ActionsMenuAction,
    ActionsMenuHeader,
    ActionsMenuSection,
} from "components/ui/actions-menu";
import { AutosavingInput } from "components/ui/autosaving-input";
import { AutosavingStateInput } from "components/ui/autosaving-state-input";
import { CalendarDateInput } from "components/ui/calendar-date";
import { ContactNameInput } from "components/ui/contact-name-input";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { SecondaryButton } from "components/ui/secondary-button";
import { Spinner } from "components/ui/spinner";
import { UnifiedTimeline } from "components/unified-timeline/unified-timeline";
import { WorkflowEntityExecutions } from "components/workflow/workflow-entity-executions";
import {
    AdvancedSearchEntityType,
    FileEntityType,
    HomeCareLeadContactRelationship,
    useGetLayoutContainerByKeyQuery,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { useCustomList } from "store/selectors/bridge";
import { useFeature, useUsers } from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { stableQueryOptions } from "util/requests";
import util from "../../util";
import { LocaleContext } from "../locale-provider/locale-provider";
import { trpc, trpcClient } from "../rpc/trpc";

type HomeCareLeadDetail = inferProcedureOutput<AppRouter["homeCareLead"]["get"]>;

const HomeCareLeadStatusSelector: React.FC<{
    client: HomeCareLeadDetail;
    onStatusChange: (newStatusId: CustomListItemId) => void;
}> = ({ client, onStatusChange }) => {
    const statusName = useStatusName(client.statusListItemId);

    return (
        <ActionBarComponent label="Status" value={statusName}>
            {({ close }) => (
                <CustomListSelect
                    label="Status"
                    customListKey={CustomListKey.HomeCareLeadStatus}
                    onChange={newStatus => {
                        close();
                        onStatusChange(newStatus as CustomListItemId);
                    }}
                    value={client.statusListItemId}
                    valueMode="id"
                />
            )}
        </ActionBarComponent>
    );
};

function useStatusName(statusListItemId: CustomListItemId): string {
    const { customList } = useCustomList(CustomListKey.HomeCareLeadStatus);
    return useMemo(() => {
        return (
            customList?.items.find(
                (x: { id: CustomListItemId; name: string }) => x.id === statusListItemId,
            )?.name || ""
        );
    }, [customList, statusListItemId]);
}

const HomeCareLeadActionBarDate: React.FC<{
    label: string;
    value: string | null;
    onChange: (newVal: string | null) => void;
}> = ({ label, value, onChange }) => {
    const display = value ? util.date.formatLongDateNoTime(value) : "(none)";
    const parsed = value ? CalendarDate.parse(value) : null;
    const initial = parsed && parsed.hasValue ? parsed.get() : null;

    return (
        <ActionBarComponent label={label} value={display}>
            {({ close }) => (
                <div className="space-y-1">
                    <CalendarDateInput
                        label={label}
                        initial={initial}
                        onCommit={d => {
                            close();
                            onChange(d ? d.toString() : null);
                        }}
                    />
                    <SecondaryButton
                        onClick={() => {
                            close();
                            onChange(null);
                        }}>
                        Clear
                    </SecondaryButton>
                </div>
            )}
        </ActionBarComponent>
    );
};

const HomeCareLeadAssignedTo: React.FC<{
    client: HomeCareLeadDetail;
    onAssignedUserChange: (userId: SelectableUser) => void;
}> = ({ client, onAssignedUserChange }) => {
    const userState = useUsers();

    let assignedUserName = "Unassigned";
    if (client.assignedUserId && userState.users[client.assignedUserId]) {
        assignedUserName = userState.users[client.assignedUserId].name;
    }

    return (
        <ActionBarComponent label="Assigned to" value={assignedUserName}>
            {({ close }) => (
                <UserSelect
                    label="Assigned to"
                    includeEveryone={false}
                    includeUnassigned={true}
                    unassignedLabel="(unassigned)"
                    value={client.assignedUserId || ""}
                    onChange={userId => {
                        close();
                        onAssignedUserChange(userId);
                    }}
                />
            )}
        </ActionBarComponent>
    );
};

const ContactFields: React.FC<{
    contact: HomeCareLeadDetail["selfContactEntity"];
    onFieldChange: (field: string, value: string) => void;
    entityId: HomeCareLeadId;
}> = ({ contact, onFieldChange, entityId }) => {
    return (
        <div className="space-y-2 md:space-y-4">
            <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                <ContactNameInput
                    name={contact.name || ""}
                    preferredName={contact.preferredName || ""}
                    onCommit={(fieldName, newValue) => onFieldChange(fieldName, newValue)}
                />
            </div>
            <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                <PhoneInputE164
                    label="Cell phone number"
                    initial={contact.cellPhone as PhoneNumberE164}
                    onCommit={newVal => onFieldChange("cellPhone", newVal)}
                    entityId={entityId}
                />
                <PhoneInputE164
                    label="Phone number (other)"
                    initial={contact.phone1 as PhoneNumberE164}
                    onCommit={newVal => onFieldChange("phone1", newVal)}
                    entityId={entityId}
                />
            </div>
            <div>
                <AutosavingInput
                    label="Email"
                    initial={contact.email1 || ""}
                    onCommit={newVal => onFieldChange("email1", newVal)}
                />
            </div>
            <div className="space-y-1 md:space-y-2">
                <div>
                    <AutosavingInput
                        label="Address"
                        initial={contact.address1 || ""}
                        onCommit={newVal => onFieldChange("address1", newVal)}
                    />
                </div>
                <LocaleContext.Consumer>
                    {locale => (
                        <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                            <AutosavingInput
                                label="City"
                                initial={contact.city || ""}
                                onCommit={newVal => onFieldChange("city", newVal)}
                            />
                            <AutosavingStateInput
                                label={locale.strings.state}
                                initial={contact.state || ""}
                                onCommit={newVal => onFieldChange("state", newVal)}
                            />
                            <AutosavingInput
                                label={locale.strings.zip}
                                initial={contact.zip || ""}
                                onCommit={newVal => onFieldChange("zip", newVal)}
                            />
                        </div>
                    )}
                </LocaleContext.Consumer>
            </div>
        </div>
    );
};

const HomeCareLeadOverview: React.FC<{
    client: HomeCareLeadDetail;
    onSelfContactFieldChange: (field: string, value: string) => void;
}> = ({ client, onSelfContactFieldChange }) => {
    return (
        <InputFormBlock>
            <div className="space-y-2 lg:space-y-4">
                <h3 className="text-lg text-gray-700">Client Info</h3>
                <ContactFields
                    contact={client.selfContactEntity}
                    onFieldChange={onSelfContactFieldChange}
                    entityId={client.id}
                />
            </div>
        </InputFormBlock>
    );
};

const HomeCareLeadBestContact: React.FC<{
    client: HomeCareLeadDetail;
    onBestContactFieldChange: (field: string, value: string) => void;
}> = ({ client, onBestContactFieldChange }) => {
    return (
        <>
            <InputFormBlock>
                <div className="space-y-2 lg:space-y-4">
                    <h3 className="text-lg text-gray-700">Best Contact Person</h3>
                    <ContactFields
                        contact={client.bestContactEntity}
                        onFieldChange={onBestContactFieldChange}
                        entityId={client.id}
                    />
                </div>
            </InputFormBlock>
            <InputFormBlock>
                <HomeCareLeadContacts
                    homeCareLeadId={client.id}
                    relationship={HomeCareLeadContactRelationship.MoreContact}
                />
            </InputFormBlock>
            <InputFormBlock>
                <HomeCareLeadContacts
                    homeCareLeadId={client.id}
                    relationship={HomeCareLeadContactRelationship.CareProvider}
                />
            </InputFormBlock>
        </>
    );
};

const HomeCareLeadCustomFields: React.FC<{
    client: HomeCareLeadDetail;
    onAnswerCommitted: () => void;
}> = ({ client, onAnswerCommitted }) => {
    const getLayoutContainer = useGetLayoutContainerByKeyQuery(
        { key: LayoutSectionParentKey.HomeCareLeadDetails },
        stableQueryOptions(),
    );

    const sections =
        getLayoutContainer.data?.getLayoutContainerByKey?.layoutSections ?? [];

    if (sections.length === 0) {
        return null;
    }

    return (
        <>
            {sections.map(section => (
                <LayoutFormSection
                    key={section.id}
                    entityId={client.id}
                    entity={client}
                    entityType="HomeCareLead"
                    layoutSection={section}
                    mode="tile"
                    answerMode={QuestionSource.Entity}
                    onAnswerCommitted={onAnswerCommitted}
                />
            ))}
        </>
    );
};

export const ShowHomeCareLead: React.FC = () => {
    const routeMatch = useRouteMatch<{ id: string; subpage?: string }>();
    const homeCareLeadId = routeMatch.params.id as HomeCareLeadId;
    const subpage = routeMatch.params.subpage;
    const isEnabled = useFeature("homeCarePrototype");
    const isWellSkyEnabled = useFeature("wellSkyIntegration");
    const { push } = useHistory();
    const queryClient = useQueryClient();
    const trpcContext = trpc.useTRPC();

    const queryOptions = trpcContext.homeCareLead.get.queryOptions({
        id: homeCareLeadId,
    });
    const { data: client, isLoading } = useQuery(queryOptions);

    const wellSkyHasCredentialQuery = useQuery(
        trpcContext.wellSkyPersonalCare.hasCredential.queryOptions(undefined, {
            enabled: isWellSkyEnabled,
        }),
    );
    const canSendToWellSky =
        isWellSkyEnabled && (wellSkyHasCredentialQuery.data?.hasCredential ?? false);

    const invalidateQuery = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
    }, [queryClient, queryOptions.queryKey]);

    const handleStatusChange = useCallback(
        async (newStatusId: CustomListItemId) => {
            await trpcClient.homeCareLead.update.mutate({
                id: homeCareLeadId,
                statusListItemId: newStatusId,
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleSelfContactFieldChange = useCallback(
        async (field: string, value: string) => {
            await trpcClient.homeCareLead.updateContact.mutate({
                id: homeCareLeadId,
                contactType: "self",
                contactEntity: { [field]: value },
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleBestContactFieldChange = useCallback(
        async (field: string, value: string) => {
            await trpcClient.homeCareLead.updateContact.mutate({
                id: homeCareLeadId,
                contactType: "bestContact",
                contactEntity: { [field]: value },
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleAssignedUserChange = useCallback(
        async (userId: SelectableUser) => {
            await trpcClient.homeCareLead.update.mutate({
                id: homeCareLeadId,
                assignedUserId: (userId || null) as UserId | null,
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleEstimatedStartDateChange = useCallback(
        async (newVal: string | null) => {
            await trpcClient.homeCareLead.update.mutate({
                id: homeCareLeadId,
                estimatedStartDate: newVal,
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleActualStartDateChange = useCallback(
        async (newVal: string | null) => {
            await trpcClient.homeCareLead.update.mutate({
                id: homeCareLeadId,
                actualStartDate: newVal,
            });
            invalidateQuery();
        },
        [homeCareLeadId, invalidateQuery],
    );

    const handleSendToWellSky = useCallback(async () => {
        if (!client) return;
        const displayName = client.selfContactEntity.name || "this home care client";
        const confirmed = await fancyConfirm(
            "Send to WellSky Personal Care?",
            `${displayName} will be created as a new prospect in your WellSky Personal Care account.`,
            "Yes, send",
            "Cancel",
        );
        if (!confirmed) return;
        await trpcClient.wellSkyPersonalCare.sendHomeCareLead.mutate({
            homeCareLeadId,
        });
        invalidateQuery();
    }, [client, homeCareLeadId, invalidateQuery]);

    const handleDelete = useCallback(async () => {
        if (!client) return;
        const displayName = client.selfContactEntity.name || "this home care client";
        const shouldDelete = await fancyConfirm(
            `Delete ${displayName}?`,
            `Really delete ${displayName}?`,
            "Yes, delete",
            "Cancel",
            { extraConfirm: true },
        );
        if (shouldDelete) {
            await trpcClient.homeCareLead.delete.mutate({
                id: homeCareLeadId,
            });
            push("/home-care-pipeline");
        }
    }, [client, homeCareLeadId, push]);

    const handleNavigate = useCallback(
        (path: string) => {
            push(`/home-care-clients/show/${homeCareLeadId}/${path}`);
        },
        [homeCareLeadId, push],
    );

    const handleCloseSection = useCallback(() => {
        push(`/home-care-clients/show/${homeCareLeadId}`);
    }, [homeCareLeadId, push]);

    const [composing, setComposing] = useState(false);
    const files = useSelector(
        (state: ApplicationState) =>
            (state.files.files && state.files.files[homeCareLeadId]) || [],
    );
    const handleCompose = useCallback(() => {
        setComposing(true);
    }, [setComposing]);

    const handleComposeDismiss = useCallback(() => {
        setComposing(false);
    }, [setComposing]);

    const renderOverview = useCallback(
        () =>
            client ? (
                <NgGrid>
                    <NgGrid.Column>
                        <HomeCareLeadOverview
                            client={client}
                            onSelfContactFieldChange={handleSelfContactFieldChange}
                        />
                        <HomeCareLeadBestContact
                            client={client}
                            onBestContactFieldChange={handleBestContactFieldChange}
                        />
                    </NgGrid.Column>
                    <NgGrid.Column>
                        <Feature name="unifiedTimeline">
                            <UnifiedTimeline
                                entityId={homeCareLeadId}
                                entityType="HomeCareLead"
                            />
                        </Feature>
                        <AntiFeature name="unifiedTimeline">
                            <Notes entityId={homeCareLeadId} entityType="HomeCareLead" />
                        </AntiFeature>
                        <HomeCareLeadReferralSource
                            homeCareLead={client}
                            invalidate={invalidateQuery}
                        />
                        <HomeCareLeadCustomFields
                            client={client}
                            onAnswerCommitted={invalidateQuery}
                        />
                        <WorkflowEntityExecutions entity={client} />
                    </NgGrid.Column>
                </NgGrid>
            ) : null,
        [
            client,
            handleSelfContactFieldChange,
            handleBestContactFieldChange,
            homeCareLeadId,
            invalidateQuery,
        ],
    );

    const renderIntake = useCallback(
        () =>
            client ? (
                <HomeCareLeadIntakeTab
                    client={client}
                    onAnswerCommitted={invalidateQuery}
                />
            ) : null,
        [client, invalidateQuery],
    );

    const renderMessages = useCallback(() => {
        return (
            <EntityConversationFeed
                entityId={homeCareLeadId}
                entityType={AdvancedSearchEntityType.HomeCareLead}
            />
        );
    }, [homeCareLeadId]);

    const renderFiles = useCallback(() => {
        return (
            <FilesControl
                entityId={homeCareLeadId}
                entityType={FileEntityType.HomeCareLead}
            />
        );
    }, [homeCareLeadId]);

    const renderActivity = useCallback(() => {
        return <Activity entityType="HomeCareLead" entityId={homeCareLeadId} />;
    }, [homeCareLeadId]);

    const renderTasks = useCallback(() => {
        return (
            <Row type="flex" className="collapsible">
                <Col className="full-width">
                    <EntityTasks
                        parentId={homeCareLeadId}
                        defaultAssigneeId={client?.assignedUserId || undefined}
                        entityInfo={{
                            displayName: client?.selfContactEntity?.name || "(no name)",
                            path: `/home-care-clients/show/${homeCareLeadId}`,
                            kind: "HomeCareLead",
                        }}
                    />
                </Col>
            </Row>
        );
    }, [homeCareLeadId, client]);

    const subpages = useMemo(
        () => [
            {
                href: `/home-care-clients/show/${homeCareLeadId}/overview`,
                linkText: "Overview",
                subpage: "overview",
                render: renderOverview,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/intake`,
                linkText: "Intake",
                subpage: "intake",
                render: renderIntake,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/emails`,
                linkText: <MessagesTabHeader />,
                subpage: "emails",
                render: renderMessages,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/tasks`,
                linkText: "Tasks",
                subpage: "tasks",
                render: renderTasks,
                badge: <TaskBadge parentId={homeCareLeadId} />,
            } as SubnavItem,
            {
                href: `/home-care-clients/show/${homeCareLeadId}/files`,
                linkText: "Files",
                subpage: "files",
                badge: (files.length || undefined) && {
                    count: files.length,
                    type: "info" as const,
                },
                render: renderFiles,
            },
            {
                href: `/home-care-clients/show/${homeCareLeadId}/activity`,
                linkText: "Activity",
                subpage: "activity",
                render: renderActivity,
            },
        ],
        [
            homeCareLeadId,
            renderOverview,
            renderIntake,
            renderMessages,
            renderTasks,
            renderFiles,
            files.length,
            renderActivity,
        ],
    );

    if (!isEnabled) {
        return null;
    }

    if (isLoading || !client) {
        return (
            <div className="flex items-center justify-center p-8">
                <Spinner />
            </div>
        );
    }

    const displayName = client.selfContactEntity.name || "(no name)";
    const bylineEntity = {
        dateAdded: new Date(client.dateAdded),
        updatedAt: new Date(client.updatedAt),
        createdBy: client.createdBy,
        updatedBy: client.updatedBy,
    };

    return (
        <div>
            <EntityPageHeader>
                <div className="flex-1">
                    <h2 className="text-xl font-semibold text-zinc-950">{displayName}</h2>
                    <NgByline entity={bylineEntity} mode="header" />
                </div>
                <div className="flex items-center gap-2">
                    <ActionsMenu>
                        <ActionsMenuSection>
                            <ActionsMenuHeader>Actions</ActionsMenuHeader>
                            <ActionsMenuAction
                                Icon={EnvelopeIcon}
                                menuHidden={false}
                                onClick={handleCompose}>
                                Send email
                            </ActionsMenuAction>
                            <ActionsMenuAction
                                Icon={PaperAirplaneIcon}
                                menuHidden={!canSendToWellSky}
                                onClick={handleSendToWellSky}>
                                Send to WellSky Personal Care
                            </ActionsMenuAction>
                            <ActionsMenuAction
                                Icon={TrashIcon}
                                menuHidden={false}
                                onClick={handleDelete}>
                                Delete
                            </ActionsMenuAction>
                        </ActionsMenuSection>
                    </ActionsMenu>
                </div>
            </EntityPageHeader>
            {composing ? (
                <MessageCompose
                    entityId={homeCareLeadId}
                    entityType={AdvancedSearchEntityType.HomeCareLead}
                    onDismiss={handleComposeDismiss}
                />
            ) : null}
            <div className="@container space-y-2 px-4 py-2">
                <div className="flex flex-wrap gap-2 md:gap-3">
                    <div>
                        <HomeCareLeadStatusSelector
                            client={client}
                            onStatusChange={handleStatusChange}
                        />
                    </div>
                    <div>
                        <HomeCareLeadAssignedTo
                            client={client}
                            onAssignedUserChange={handleAssignedUserChange}
                        />
                    </div>
                    <div>
                        <HomeCareLeadActionBarDate
                            label="Estimated start"
                            value={client.estimatedStartDate}
                            onChange={handleEstimatedStartDateChange}
                        />
                    </div>
                    <div>
                        <HomeCareLeadActionBarDate
                            label="Start of care"
                            value={client.actualStartDate}
                            onChange={handleActualStartDateChange}
                        />
                    </div>
                    <div className="inline-block lg:hidden">
                        <NgByline entity={bylineEntity} mode="action-bar" />
                    </div>
                </div>
                {client.wellSkyClientUrl ? (
                    <a
                        href={client.wellSkyClientUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700">
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                        Open in WellSky Personal Care
                    </a>
                ) : null}
            </div>
            <DetailPageLayout>
                <Subnav
                    subpages={subpages}
                    selectedSubpage={subpage}
                    defaultSubpage="overview"
                    navigate={handleNavigate}
                    closeSection={handleCloseSection}
                />
            </DetailPageLayout>
        </div>
    );
};
